/**
 * useSmsConsent Hook
 * AIDLC Administrative Backend
 *
 * Manages SMS consent state, persisted to localStorage.
 * Tracks whether the user has consented, their phone number,
 * and the timestamp of consent. Provides modal visibility control.
 */

import { useState, useCallback } from 'react';

const STORAGE_KEY = 'ldaja_sms_consent';

interface SmsConsentState {
  consented: boolean;
  phoneNumber: string;
  consentedAt: string | null;
}

function loadConsent(): SmsConsentState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.consented === 'boolean') {
        return parsed;
      }
    }
  } catch {
    // Corrupted storage — reset
  }
  return { consented: false, phoneNumber: '', consentedAt: null };
}

function saveConsent(state: SmsConsentState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage full or unavailable — degrade gracefully
  }
}

export function useSmsConsent() {
  const [consent, setConsent] = useState<SmsConsentState>(loadConsent);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const grantConsent = useCallback((phoneNumber: string) => {
    const state: SmsConsentState = {
      consented: true,
      phoneNumber,
      consentedAt: new Date().toISOString(),
    };
    setConsent(state);
    saveConsent(state);
    setIsModalOpen(false);
  }, []);

  const revokeConsent = useCallback(() => {
    const state: SmsConsentState = {
      consented: false,
      phoneNumber: '',
      consentedAt: null,
    };
    setConsent(state);
    saveConsent(state);
  }, []);

  return {
    hasConsented: consent.consented,
    consentedPhoneNumber: consent.phoneNumber,
    consentedAt: consent.consentedAt,
    isModalOpen,
    openModal,
    closeModal,
    grantConsent,
    revokeConsent,
  };
}
